import { memo, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux";
import { selectSnapshots } from "../../selectors/security";
import { getSecurityAsync, getSecurityKey } from "../../util/security";
import { toLocaleStringIn } from "../../util/general";
import { Box, Divider, Grid, Paper, Typography } from '@mui/material'
import Positions from "../../assets/icons/Positions";
import TradingAccountSelector from "../tradingAccountStatus/TradingAccountSelector";
import PositionsTable from "../positionsTable/positionsTable";
import { Position } from "../../proto/generated/position";
import EmptyView from "../postionsWidget/EmptyView";
import GenericTabsSmall from "../genericTabs/GenericTabsSmall";
import { PositionNavButtons, navItems } from "../postionsWidget";


export type PositionsSidebarWidgetProps = {
    positions: Position[]
}

const PositionsSidebarWidget: React.FC<PositionsSidebarWidgetProps> = ({positions}) => {
    const snapshots = useSelector(selectSnapshots);
    const [positionTableViewType, setPositionTableViewType] = useState(PositionNavButtons.PNL)

    const [totalPnLPair, setTotalPnLPair] = useState<[number, number]>([0, 0])
    const positionKeys = useMemo(() => positions.map(p => getSecurityKey(p.security.exchange, p.security.code)), [positions])
    useEffect(() => {
        if (!positionKeys.length) { setTotalPnLPair([0, 0]); return }
        Promise.all(positionKeys.map(k => getSecurityAsync(k))).then((securities) => {
            const securityMap = new Map<string, Awaited<ReturnType<typeof getSecurityAsync>>>()
            positionKeys.forEach((k, i) => { if (securities[i]) securityMap.set(k, securities[i]!) })
            let unrealisedProfit = 0
            let realisedProfit = 0
            positions.forEach((position) => {
                const securityKey = getSecurityKey(position.security.exchange, position.security.code)
                const security = securityMap.get(securityKey)
                const snapshot = snapshots[securityKey]
                const ltp = snapshot?.lastTradedPrice || 0
                const qty = position.closePosition
                if (qty !== 0) {
                    const avgPrice = position.closePositionValue/qty
                    const pnl = (ltp - avgPrice)*qty
                    unrealisedProfit += pnl/(security?.priceMultiplier??100)
                }
                realisedProfit += (position.realisedProfit??0)/(security?.priceMultiplier??100)
            })
            setTotalPnLPair([unrealisedProfit, realisedProfit])
        })
    }, [positionKeys.join(','), positions, snapshots])

    return  <>
            <Paper sx={{ position: 'sticky', top: 0, zIndex: 10, borderRadius: 0, width: '100%'}}>
                <Grid container px={'8px'} py={0}
                    justifyContent={'space-between'}
                    alignItems={'center'}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Positions sx={{ color: (theme) => theme.palette.text.secondary }} />
                        <Typography variant={'body1'}>Positions</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {positions.length ? (
                            <Typography className="font-feature-num" variant="caption" sx={{ color: (theme) => theme.palette.text.secondary, mr: 1, lineHeight: (theme) => theme.typography.body1.lineHeight }}>
                                {(() => {
                                    const [unrealisedProfit, realisedProfit] = totalPnLPair
                                    return (
                                        <>
                                            REALISED:{" "}
                                            <span
                                                className={
                                                    realisedProfit === 0
                                                        ? undefined
                                                        : realisedProfit > 0
                                                        ? "green-text"
                                                        : "red-text"
                                                }
                                            >
                                                {toLocaleStringIn(realisedProfit)}
                                            </span>
                                            {" | TOTAL: "}
                                            <span
                                                className={
                                                    (realisedProfit + unrealisedProfit) === 0
                                                        ? undefined
                                                        : (realisedProfit + unrealisedProfit) > 0
                                                        ? "green-text"
                                                        : "red-text"
                                                }
                                            >
                                                {toLocaleStringIn(realisedProfit + unrealisedProfit)}
                                            </span>
                                        </>
                                    )
                                })()}
                            </Typography>
                        ) : null}
                        <TradingAccountSelector compact />
                    </Box>
                </Grid>
                <Divider style={{ width: '100%' }} />
                {positions.length > 0 && (
                    <Grid container px={'8px'} py={1} alignItems={'center'} justifyContent={'flex-end'}>
                        <GenericTabsSmall<PositionNavButtons>
                            items={navItems}
                            onChange={(val) => {
                                if (val) setPositionTableViewType(val)
                            }}
                            value={positionTableViewType}
                            tabProps={{ sx: { minWidth: 50, fontSize: '12px' } }}
                        />
                    </Grid>
                )}
            </Paper>
            <Box sx={{ overflowX: 'auto', width: '100%', height:'100%' }}>
                {positions.length === 0  ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 0 }}>
                        <EmptyView text={
                            "Your intraday and carry-forward positions will appear here"
                        } />
                    </Box>) :
                <PositionsTable positions={positions} showGreeks={positionTableViewType === PositionNavButtons.GREEKS} />}
            </Box>
        </>
}

export default memo(PositionsSidebarWidget);